import { useFragment } from '@apollo/client';
import { Box, Stack, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { DynamicIcon, SidePanelCard } from 'src/components';
import { ClientMenu } from 'src/components/ClientComponents/client-menu';
import { useClientQuickActions } from 'src/components/ClientComponents/use-client-quick-actions';
import { DeletedRecordAlert } from 'src/components/DeletedRecordAlert';
import { getEntityCardIconSx, getSidePanelCardsSx } from 'src/components/EntitySidePanel';
import { BlockContainer } from 'src/components/Layout/named-layout-block.view';
import { PersonalizedSections } from 'src/components/SidePanelPersonalization';
import { PersonalizeSidePanelButton } from 'src/components/SidePanelPersonalization/personalize-side-panel-button';
import { ClientRelatedTabs } from 'src/components/SidePanelTabs/ClientTab/client-related-tabs';
import { resolvePartnerTabContactInfo } from 'src/components/SidePanelTabs/PartnerTab/partner-contact-info';
import { PartnerInfo } from 'src/components/SidePanelTabs/PartnerTab/partner-info';
import { PartnerInfoTabs } from 'src/components/SidePanelTabs/PartnerTab/partner-info-tabs';
import { ClientCard } from 'src/components/SidePanelTabs/SiteClient/client-card';
import { gql } from 'src/gql';
import { useGroupRules, useRestoreClient, UserGroupRules } from 'src/graphql';
import { CustomAttributesSidePanelProvider } from 'src/graphql/entities/custom-attributes/side-panel-provider';
import { SidePanelPersonalizationProvider } from 'src/graphql/entities/side-panel-personalization';
import { CrossPartnerUserSettingsProvider } from 'src/graphql/entities/side-panel-personalization/cross-partner-user-settings-provider';
import { clientSidePanelSurface } from 'src/pages/Clients/SidePanel/client-surface';
import { PartnerCard } from 'src/pages/Conversations/SidePanel/partner-card';
import { SidePanelToolsSlotProvider } from 'src/pages/Conversations/SidePanel/side-panel-tools-slot';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import { useScreenDimensions } from 'src/utils/system';

const ClientSidePanelFragment = gql(`
  fragment ClientSidePanel_Client on Client {
    id
    clientId
    partnerId
    firstName
    lastName
    fullName @client
    isDeleted @client
    deletedAt
    primaryEmail {
      id
      value
    }
    partner {
      id
      partnerId
      businessName
      timeZone
      supportPhone
      supportEmail
      membershipEmail
      supportLiveDate
      litsDate
      partnerOnesheet {
        id
        partnerId
        mainOfficePhone
        asTransferForBusinessHoursOnly
        internalSupportNotes
        escalationPolicy
        recentAnnouncements
        whatIsYourEquipmentRepairPolicy
        primaryRsmTool
      }
      brandGroups {
        id
        brandGroupId
        fwdPhonePublished
        fwdPhonePublishedExt
        fwdPhoneSupport
        supportEmail
        membershipEmail
      }
    }
    ...ClientCard_Client
    ...ClientMenu_Client
    ...ClientRelatedTabs_Client
    ...UseClientQuickActions_Client
  }
`);

interface ClientSidePanelProps {
  clientId: string;
  // hosts now own the panel chrome (and its close control) directly; kept for prop-contract compatibility
  // eslint-disable-next-line react/no-unused-prop-types
  onClose?: () => void;
  extendedView?: boolean;
  /** Sticky node in the scrolling column that hosts the floating panel tools. */
  toolsSlot?: HTMLElement | null;
}

const ClientCardBody = ({
  clientId,
  isDeleted,
  deletedAt,
  extendedView,
}: {
  clientId: string;
  isDeleted: boolean;
  deletedAt?: string | null;
  extendedView?: boolean;
}) => {
  const { isMobile } = useScreenDimensions();
  const { checkValidity } = useGroupRules();
  const { restoreClient } = useRestoreClient();

  const onRestoreClientClick = useCallback(async () => {
    await restoreClient(clientId);
  }, [restoreClient, clientId]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
        <PersonalizeSidePanelButton />
        {isMobile ? <ClientMenu clientId={clientId} /> : null}
      </Stack>

      <ClientCard
        clientId={clientId}
        hideHeader
        showMoreButton={!extendedView}
        sx={{
          border: 'none',
          borderRadius: 0,
          boxShadow: 'none',
          backgroundColor: 'transparent',
          backgroundImage: 'none',
          overflow: 'visible',
        }}
      />

      {isDeleted && !extendedView ? (
        <DeletedRecordAlert
          entityLabel="client"
          deletedAt={deletedAt}
          canRestore={checkValidity([UserGroupRules.AccessToRestoreClients])}
          onRestore={onRestoreClientClick}
        />
      ) : null}

      {/* attribute rows continue the card's property rhythm — cancel the wrapper
          Stack's spacing step (same treatment as the labels Box in SiteTab) */}
      <Box sx={{ marginTop: (theme) => theme.utils.important(0) }}>
        <PersonalizedSections />
      </Box>

      {!extendedView && <ClientRelatedTabs clientId={clientId} variant="scrollable" />}
    </Stack>
  );
};

export const ClientSidePanel = ({
  extendedView,
  clientId,
  toolsSlot,
}: ClientSidePanelProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenDimensions();
  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { groupRules, checkValidity } = useGroupRules();
  // without a slot the personalize button falls back to rendering inline in the card,
  // so the clearance above the cards would reserve room for nothing
  const hasFloatingTools =
    !!toolsSlot && checkValidity([UserGroupRules.AccessToPersonalizeSidepanel]);
  const canAccessToPartnerTab = !!groupRules[UserGroupRules.AccessToPartnerTab];
  const canSeePartnersTab =
    canAccessToPartnerTab || (isMultiTenancyMode && selectedPartnerId === null);

  const { data: client, complete } = useFragment({
    fragment: ClientSidePanelFragment,
    fragmentName: 'ClientSidePanel_Client',
    from: {
      __typename: 'Client',
      id: clientId,
    },
  });

  const partnerWithBrandGroupContactInfo = useMemo(
    () => resolvePartnerTabContactInfo(client.partner),
    [client.partner],
  );

  const { setModalProps: setAddSiteModalProps } = useModalMethods(Modals.AddSiteModal);

  useEffect(() => {
    if (!complete) {
      return;
    }
    return setAddSiteModalProps({
      client: {
        partnerId: client.partnerId,
        id: client.clientId,
        name: client.fullName,
        email: client.primaryEmail?.value,
      },
    });
  });

  useClientQuickActions({
    clientId,
  });

  if (!complete) {
    return null;
  }

  return (
    <CrossPartnerUserSettingsProvider>
      <CustomAttributesSidePanelProvider
        entity="Client"
        entityId={client.clientId}
        partnerId={client.partner?.partnerId ?? client.partnerId}
        canEdit={!client.isDeleted}
      >
        <SidePanelPersonalizationProvider surface={clientSidePanelSurface}>
          <BlockContainer
            // the cards inside carry the only frame, so the column itself stays bare
            border="none !important"
            sx={{
              height: 'auto',
              // the collapsing wrapper sets nowrap for its width animation; undo it here or the content outgrows the column
              whiteSpace: 'normal',
            }}
          >
            <SidePanelToolsSlotProvider value={toolsSlot ?? null}>
              <Stack
                data-testid="client-side-panel-cards"
                gap={1}
                sx={getSidePanelCardsSx({ hasFloatingTools, extendedView, isMobile })}
              >
                {canSeePartnersTab && client.partner ? (
                  <PartnerCard partner={client.partner} data-testid="client-partner-card">
                    <Stack spacing={2}>
                      <PartnerInfo partner={partnerWithBrandGroupContactInfo} />
                      {partnerWithBrandGroupContactInfo ? (
                        <PartnerInfoTabs partner={partnerWithBrandGroupContactInfo} />
                      ) : null}
                    </Stack>
                  </PartnerCard>
                ) : null}
                <SidePanelCard
                  data-testid="client-details-card"
                  defaultOpen
                  leading={
                    <DynamicIcon
                      name="user"
                      library="lucide"
                      sx={getEntityCardIconSx(theme.palette.custom.OV.entity.client)}
                    />
                  }
                  title={client.fullName}
                  titleTooltip={client.fullName ?? undefined}
                >
                  <ClientCardBody
                    clientId={clientId}
                    isDeleted={!!client.isDeleted}
                    deletedAt={client.deletedAt}
                    extendedView={extendedView}
                  />
                </SidePanelCard>
              </Stack>
            </SidePanelToolsSlotProvider>
          </BlockContainer>
        </SidePanelPersonalizationProvider>
      </CustomAttributesSidePanelProvider>
    </CrossPartnerUserSettingsProvider>
  );
};
